import React, {useEffect, useRef, useState} from "react";
import SpeechRecognition, {
    useSpeechRecognition
} from "react-speech-recognition";

import axios from "axios";

import {
    Button,
    Form,
    InputGroup,
    OverlayTrigger,
    Tooltip
} from "react-bootstrap";

import {baseUrl} from "../Univ";

import "./AIChat.css";


const AIChat = ({agentCode = "AGRICULTURE"}) => {

    // =========================================================
    // STATES
    // =========================================================

    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [message, setMessage] = useState("");

    const [sessionId, setSessionId] = useState("");

    const [selectedFile, setSelectedFile] = useState(null);

    const [inputLanguage, setInputLanguage] =
        useState("en-IN");

    const [voiceLanguage, setVoiceLanguage] =
        useState("en-IN");

    const [voiceInputEnabled, setVoiceInputEnabled] =
        useState(false);

    const [voiceEnabled, setVoiceEnabled] =
        useState(true);

    const [lastAIReply, setLastAIReply] =
        useState("");


    const [messages, setMessages] = useState([
        {
            sender: "ai",
            text:
                "👋 Welcome to eAgri ERP AI Assistant.\n\nHow can I help you today?"
        }
    ]);


    // =========================================================
    // REFS
    // =========================================================

    const fileInputRef = useRef(null);

    const chatEndRef = useRef(null);

    const silenceTimerRef = useRef(null);


    // =========================================================
    // SPEECH RECOGNITION
    // =========================================================

    const {
        transcript,
        listening,
        resetTranscript
    } = useSpeechRecognition();


    // =========================================================
    // AUTO SCROLL
    // =========================================================

    useEffect(() => {

        chatEndRef.current?.scrollIntoView({
            behavior: "smooth"
        });

    }, [messages]);


    // =========================================================
    // VOICE TRANSCRIPT
    // =========================================================

    useEffect(() => {

        if (!voiceInputEnabled) {
            return;
        }

        if (!transcript) {
            return;
        }


        setMessage(transcript);


        if (silenceTimerRef.current) {

            clearTimeout(
                silenceTimerRef.current
            );

        }


        silenceTimerRef.current =
            setTimeout(() => {

                const spokenText =
                    transcript.trim();

                if (spokenText) {

                    sendMessage(
                        spokenText
                    );

                }

                resetTranscript();

                setMessage("");

            }, 5000);


        return () => {

            if (silenceTimerRef.current) {

                clearTimeout(
                    silenceTimerRef.current
                );

            }

        };

    }, [transcript, voiceInputEnabled]);


    // =========================================================
    // START VOICE
    // =========================================================

    const startVoice = () => {

        if (loading) {
            return;
        }

        setVoiceInputEnabled(true);

        resetTranscript();

        SpeechRecognition.startListening({
            continuous: true,
            interimResults: true,
            language: inputLanguage
        });

    };


    // =========================================================
    // TOGGLE SPEAKER
    // =========================================================

    const toggleVoice = () => {

        if (voiceEnabled) {

            window.speechSynthesis.cancel();

            setVoiceEnabled(false);

        } else {

            setVoiceEnabled(true);

            if (lastAIReply) {

                speak(lastAIReply);

            }

        }

    };


    // =========================================================
    // CLEAN TEXT
    // =========================================================

    const cleanForSpeech = (text) => {

        if (
            typeof text !== "string"
        ) {
            text = String(text || "");
        }

        return text

            .replace(
                /```[\s\S]*?```/g,
                ""
            )

            .replace(
                /[*#>`]/g,
                ""
            )

            .replace(
                /\[(.*?)\]\((.*?)\)/g,
                "$1"
            )

            .replace(
                /[📦📂📋🚚⚖📊⚠•]/g,
                ""
            )

            .replace(
                /[=]{2,}/g,
                ""
            )

            .replace(
                /[-]{2,}/g,
                ""
            )

            .replace(
                /\n+/g,
                ". "
            )

            .replace(
                /\s+/g,
                " "
            )

            .trim();

    };


    // =========================================================
    // FORMAT NUMBERS
    // =========================================================

    const formatForSpeech = (text) => {

        if (
            typeof text !== "string"
        ) {
            text = String(text || "");
        }


        // Mobile numbers

        text = text.replace(
            /\b\d{10}\b/g,
            (num) => {

                return num
                    .split("")
                    .join(" ");

            }
        );


        // OTP

        text = text.replace(
            /\b\d{4,8}\b/g,
            (num) => {

                return num
                    .split("")
                    .join(" ");

            }
        );


        // Invoice numbers

        text = text.replace(
            /\b([A-Za-z]+)(\d+)\b/g,
            (_, letters, digits) => {

                return (
                    letters +
                    " " +
                    digits
                        .split("")
                        .join(" ")
                );

            }
        );


        return text;

    };


    // =========================================================
    // SPEAK
    // =========================================================

    const speak = (text) => {

        if (!voiceEnabled) {
            return;
        }


        if (
            !window.speechSynthesis
        ) {
            return;
        }


        window.speechSynthesis.cancel();


        const voices =
            window.speechSynthesis.getVoices();


        let selectedVoice =
            voices.find(
                (voice) =>
                    voice.lang ===
                    voiceLanguage
            );


        if (!selectedVoice) {

            selectedVoice =
                voices.find(
                    (voice) =>
                        voice.lang &&
                        voice.lang.startsWith(
                            voiceLanguage.split("-")[0]
                        )
                );

        }


        const speech =
            new SpeechSynthesisUtterance(
                formatForSpeech(
                    cleanForSpeech(text)
                )
            );


        speech.lang =
            voiceLanguage;


        if (selectedVoice) {

            speech.voice =
                selectedVoice;

        }


        speech.rate = 1;

        speech.pitch = 1;

        speech.volume = 1;


        window.speechSynthesis.speak(
            speech
        );

    };


    // =========================================================
    // SPEECH VOICES
    // =========================================================

    useEffect(() => {

        const loadVoices = () => {

            window.speechSynthesis
                .getVoices();

        };


        loadVoices();


        window.speechSynthesis
            .addEventListener(
                "voiceschanged",
                loadVoices
            );


        return () => {

            window.speechSynthesis
                .removeEventListener(
                    "voiceschanged",
                    loadVoices
                );

        };

    }, []);


    // =========================================================
    // COPY
    // =========================================================

    const copyMessage = async (text) => {

        try {

            await navigator.clipboard.writeText(
                text
            );

        } catch (error) {

            console.error(
                "Copy failed:",
                error
            );

        }

    };


    // =========================================================
    // SEND MESSAGE
    // =========================================================

    const sendMessage = async (
        voiceMessage = null
    ) => {

        console.log(
            "================================="
        );

        console.log(
            "AI CHAT SEND"
        );

        console.log(
            "Voice message:",
            voiceMessage
        );

        console.log(
            "Typed message:",
            message
        );

        console.log(
            "Selected file:",
            selectedFile
        );

        console.log(
            "Session:",
            sessionId
        );

        console.log(
            "Agent:",
            agentCode
        );

        console.log(
            "Base URL:",
            baseUrl
        );

        console.log(
            "================================="
        );


        // =====================================================
        // STOP VOICE
        // =====================================================

        setVoiceInputEnabled(false);

        SpeechRecognition.stopListening();


        // =====================================================
        // PREVENT DUPLICATE REQUEST
        // =====================================================

        if (loading) {

            console.log(
                "Already processing request"
            );

            return;

        }


        // =====================================================
        // GET TEXT
        // =====================================================

        let text = "";


        if (
            typeof voiceMessage === "string"
        ) {

            text =
                voiceMessage.trim();

        } else {

            text =
                typeof message === "string"
                    ? message.trim()
                    : "";

        }


        console.log(
            "FINAL TEXT:",
            text
        );


        // =====================================================
        // NOTHING TO SEND
        // =====================================================

        if (
            !text &&
            !selectedFile
        ) {

            console.log(
                "Nothing to send"
            );

            return;

        }


        // =====================================================
        // SHOW USER MESSAGE
        // =====================================================

        const displayText =
            text ||
            (
                selectedFile
                    ? `📎 ${selectedFile.name}`
                    : ""
            );


        setMessages(
            (previous) => [
                ...previous,
                {
                    sender: "user",
                    text: displayText
                }
            ]
        );


        // =====================================================
        // CLEAR INPUT
        // =====================================================

        setMessage("");


        setLoading(true);


        try {

            let response;


            // =================================================
            // FILE REQUEST
            // =================================================

            if (selectedFile) {

                const formData =
                    new FormData();


                formData.append(
                    "question",
                    text
                );


                formData.append(
                    "session_id",
                    sessionId || ""
                );


                formData.append(
                    "agent_code",
                    agentCode || "GENERAL"
                );


                formData.append(
                    "file",
                    selectedFile
                );


                console.log(
                    "POSTING FILE REQUEST"
                );


                response =
                    await axios.post(
                        baseUrl +
                        "ai/chat/",
                        formData
                    );

            }


            // =================================================
            // NORMAL TEXT REQUEST
            // =================================================

            else {

                const payload = {

                    question: text,

                    session_id:
                        sessionId || "",

                    agent_code:
                        agentCode || "GENERAL"

                };


                console.log(
                    "POSTING JSON:",
                    payload
                );


                response =
                    await axios.post(
                        baseUrl +
                        "ai/chat/",
                        payload,
                        {
                            headers: {
                                "Content-Type":
                                    "application/json"
                            }
                        }
                    );

            }


            // =================================================
            // RESPONSE
            // =================================================

            console.log(
                "AI API STATUS:",
                response.status
            );


            console.log(
                "AI API RESPONSE:",
                response.data
            );


            // =================================================
            // SESSION
            // =================================================

            if (
                response.data &&
                response.data.session_id
            ) {

                setSessionId(
                    response.data.session_id
                );

            }


            // =================================================
            // EXTRACT AI RESPONSE
            // =================================================

            let aiReply = "";


            if (
                response.data === null ||
                response.data === undefined
            ) {

                aiReply =
                    "The server returned an empty response.";

            }


            else if (
                typeof response.data ===
                "string"
            ) {

                aiReply =
                    response.data;

            }


            else if (
                typeof response.data.response ===
                "string"
            ) {

                aiReply =
                    response.data.response;

            }


            else if (
                response.data.response &&
                typeof response.data.response ===
                "object"
            ) {

                const nested =
                    response.data.response;


                if (
                    typeof nested.response ===
                    "string"
                ) {

                    aiReply =
                        nested.response;

                } else {

                    aiReply =
                        JSON.stringify(
                            nested,
                            null,
                            2
                        );

                }

            }


            else if (
                response.data.message &&
                typeof response.data.message ===
                "string"
            ) {

                aiReply =
                    response.data.message;

            }


            else {

                aiReply =
                    JSON.stringify(
                        response.data,
                        null,
                        2
                    );

            }


            // =================================================
            // FINAL SAFETY
            // =================================================

            if (
                !aiReply ||
                !aiReply.trim()
            ) {

                aiReply =
                    "No response received from AI.";

            }


            console.log(
                "FINAL AI REPLY:",
                aiReply
            );


            // =================================================
            // DISPLAY AI MESSAGE
            // =================================================

            setMessages(
                (previous) => [
                    ...previous,
                    {
                        sender: "ai",
                        text: aiReply
                    }
                ]
            );


            setLastAIReply(
                aiReply
            );


            // =================================================
            // SPEAK
            // =================================================

            speak(
                aiReply
            );


            // =================================================
            // CLEAR FILE
            // =================================================

            setSelectedFile(
                null
            );


            if (
                fileInputRef.current
            ) {

                fileInputRef.current.value =
                    "";

            }

        } catch (error) {

            console.error(
                "================================="
            );

            console.error(
                "AI CHAT ERROR"
            );

            console.error(
                error
            );

            console.error(
                "STATUS:",
                error.response?.status
            );

            console.error(
                "DATA:",
                error.response?.data
            );

            console.error(
                "MESSAGE:",
                error.message
            );

            console.error(
                "================================="
            );


            let errorMessage =
                "❌ Unable to process request.";


            if (
                error.response?.data
            ) {

                if (
                    typeof error.response.data ===
                    "string"
                ) {

                    errorMessage =
                        "❌ " +
                        error.response.data;

                }

                else if (
                    error.response.data.error
                ) {

                    errorMessage =
                        "❌ " +
                        error.response.data.error;

                }

                else if (
                    error.response.data.message
                ) {

                    errorMessage =
                        "❌ " +
                        error.response.data.message;

                }

            }


            setMessages(
                (previous) => [
                    ...previous,
                    {
                        sender: "ai",
                        text: errorMessage
                    }
                ]
            );

        } finally {

            setLoading(false);

        }

    };


    // =========================================================
    // CLEAR CHAT
    // =========================================================

    const clearChat = () => {

        window.speechSynthesis.cancel();

        setSessionId("");

        setMessages([
            {
                sender: "ai",
                text:
                    "👋 Welcome to eAgri ERP AI Assistant.\n\nHow can I help you today?"
            }
        ]);

        setMessage("");

        setSelectedFile(null);

        resetTranscript();

        setLastAIReply("");

        if (fileInputRef.current) {

            fileInputRef.current.value =
                "";

        }

    };


    // =========================================================
    // FILE SELECT
    // =========================================================

    const chooseFile = () => {

        if (fileInputRef.current) {

            fileInputRef.current.click();

        }

    };


    // =========================================================
    // FILE SELECTED
    // =========================================================

    const fileSelected = (event) => {

        if (
            !event.target.files ||
            !event.target.files.length
        ) {

            return;

        }


        const file =
            event.target.files[0];


        console.log(
            "Selected file:",
            file
        );


        setSelectedFile(file);

    };


    // =========================================================
    // REMOVE FILE
    // =========================================================

    const removeFile = () => {

        setSelectedFile(null);

        if (fileInputRef.current) {

            fileInputRef.current.value =
                "";

        }

    };


    // =========================================================
    // FEEDBACK
    // =========================================================

    const likeMessage = async (index) => {

        console.log(
            "Liked:",
            index
        );

    };


    const dislikeMessage = async (index) => {

        console.log(
            "Disliked:",
            index
        );

    };


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <>

            {/* =================================================
                FLOAT BUTTON
            ================================================= */}

            {!open && (

                <button
                    className="ai-float-btn"
                    onClick={() =>
                        setOpen(true)
                    }
                >

                    🤖

                </button>

            )}


            {/* =================================================
                CHAT WINDOW
            ================================================= */}

            {open && (

                <div className="ai-chat-window">


                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="ai-chat-header">

                        <div className="ai-header-left">

                            <div className="ai-header-icon">
                                🤖
                            </div>


                            <div className="ai-header-text">

                                <div className="ai-header-title">
                                    eAgri AI Assistant
                                </div>


                                <div className="ai-header-subtitle">

                                    {loading
                                        ? (
                                            <>
                                                🟢 Thinking...
                                            </>
                                        )
                                        : (
                                            <>
                                                🟢 eAgri AI • Online
                                            </>
                                        )}

                                </div>

                            </div>

                        </div>


                        <div className="ai-header-actions">


                            {/* VOICE REPLY */}

                            <button
                                className="ai-header-btn"
                                title="Voice Reply"
                                onClick={
                                    toggleVoice
                                }
                            >

                                {voiceEnabled
                                    ? "🔊"
                                    : "🔇"}

                            </button>


                            {/* VOICE INPUT */}

                            <button
                                className="ai-header-btn"
                                title="Voice Input"
                                onClick={() => {

                                    if (
                                        voiceInputEnabled
                                    ) {

                                        setVoiceInputEnabled(
                                            false
                                        );

                                        SpeechRecognition
                                            .stopListening();

                                    } else {

                                        startVoice();

                                    }

                                }}
                            >

                                {voiceInputEnabled
                                    ? "🛑"
                                    : "🎤"}

                            </button>


                            {/* VOICE LANGUAGE */}

                            <select
                                value={
                                    voiceLanguage
                                }
                                onChange={(event) => {

                                    setVoiceLanguage(
                                        event.target.value
                                    );

                                }}
                            >

                                <option value="en-IN">
                                    English
                                </option>

                                <option value="ta-IN">
                                    தமிழ்
                                </option>

                                <option value="hi-IN">
                                    हिन्दी
                                </option>

                            </select>


                            {/* CLEAR */}

                            <button
                                className="ai-header-btn"
                                title="Clear Chat"
                                onClick={
                                    clearChat
                                }
                            >

                                🗑️

                            </button>


                            {/* CLOSE */}

                            <button
                                className="ai-header-btn"
                                title="Close"
                                onClick={() =>
                                    setOpen(false)
                                }
                            >

                                ✕

                            </button>

                        </div>

                    </div>


                    {/* =================================================
                        CHAT BODY
                    ================================================= */}

                    <div className="ai-chat-body">

                        {messages.map(
                            (msg, index) => (

                                <div
                                    key={index}
                                    className={
                                        msg.sender === "user"
                                            ? "message-row user-row"
                                            : "message-row ai-row"
                                    }
                                >


                                    {/* AI AVATAR */}

                                    {msg.sender === "ai" && (

                                        <div className="avatar ai-avatar">
                                            🤖
                                        </div>

                                    )}


                                    {/* MESSAGE */}

                                    <div
                                        className={
                                            msg.sender === "user"
                                                ? "user-bubble"
                                                : "ai-bubble"
                                        }
                                    >

                                        <div className="message-text">

                                            {msg.text}

                                        </div>


                                        <div className="message-footer">

                                            <small>

                                                {new Date()
                                                    .toLocaleTimeString(
                                                        [],
                                                        {
                                                            hour: "2-digit",
                                                            minute: "2-digit"
                                                        }
                                                    )}

                                            </small>


                                            {/* AI ACTIONS */}

                                            {msg.sender === "ai" && (

                                                <div>

                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                Copy
                                                            </Tooltip>
                                                        }
                                                    >

                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            onClick={() =>
                                                                copyMessage(
                                                                    msg.text
                                                                )
                                                            }
                                                        >

                                                            📋

                                                        </Button>

                                                    </OverlayTrigger>


                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() =>
                                                            likeMessage(
                                                                index
                                                            )
                                                        }
                                                    >

                                                        👍

                                                    </Button>


                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={() =>
                                                            dislikeMessage(
                                                                index
                                                            )
                                                        }
                                                    >

                                                        👎

                                                    </Button>

                                                </div>

                                            )}

                                        </div>

                                    </div>


                                    {/* USER AVATAR */}

                                    {msg.sender === "user" && (

                                        <div className="avatar user-avatar">
                                            👤
                                        </div>

                                    )}

                                </div>

                            )
                        )}


                        {/* =================================================
                            THINKING
                        ================================================= */}

                        {loading && (

                            <div className="message-row ai-row">

                                <div className="avatar ai-avatar">
                                    🤖
                                </div>


                                <div className="typing-bubble">

                                    <span></span>
                                    <span></span>
                                    <span></span>

                                </div>

                            </div>

                        )}


                        <div
                            ref={chatEndRef}
                        />

                    </div>


                    {/* =================================================
                        FILE INPUT
                    ================================================= */}

                    <div>

                        <input
                            type="file"
                            ref={fileInputRef}
                            hidden
                            accept="
                                image/*,
                                .pdf,
                                .doc,
                                .docx,
                                .xls,
                                .xlsx,
                                .csv,
                                .txt
                            "
                            onChange={
                                fileSelected
                            }
                        />


                        {/* SELECTED FILE */}

                        {selectedFile && (

                            <div className="selected-file">

                                📎

                                <strong>
                                    {selectedFile.name}
                                </strong>


                                <Button
                                    size="sm"
                                    variant="danger"
                                    className="ms-2"
                                    onClick={
                                        removeFile
                                    }
                                >

                                    ✕

                                </Button>

                            </div>

                        )}


                        {/* =================================================
                            INPUT
                        ================================================= */}

                        <InputGroup>


                            {/* FILE */}

                            <Button
                                variant="secondary"
                                onClick={
                                    chooseFile
                                }
                            >

                                📎

                            </Button>


                            {/* TEXT */}

                            <Form.Control

                                type="text"

                                placeholder="Ask anything about eAgri ERP..."

                                value={message}

                                disabled={loading}

                                autoComplete="off"

                                onChange={(event) => {

                                    const value =
                                        event.target.value;

                                    console.log(
                                        "INPUT:",
                                        value
                                    );

                                    setMessage(
                                        value
                                    );

                                }}


                                onKeyDown={(event) => {

                                    if (
                                        event.key ===
                                        "Enter"
                                    ) {

                                        event.preventDefault();

                                        if (!loading) {

                                            sendMessage();

                                        }

                                    }

                                }}

                            />


                            {/* SEND */}

                            <Button
                                variant="success"
                                disabled={
                                    loading ||
                                    (
                                        !message.trim() &&
                                        !selectedFile
                                    )
                                }
                                onClick={() =>
                                    sendMessage()
                                }
                            >

                                {loading
                                    ? "..."
                                    : "Send"}

                            </Button>

                        </InputGroup>

                    </div>

                </div>

            )}

        </>

    );

};


export default AIChat;